import './style.scss';

import { faCalendar, faSignalStream, faSignalStreamSlash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Menu, TextField } from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import InputMask from 'react-input-mask';

import CustomCalendar from '../../../calendar/calendar';

interface CalendarProps {
  selected?: boolean;
  handleChangeDatetime: (timestamp: string) => void;
  isLive: boolean;
  reset: () => void;
  displayTimestamp?: string;
  initialTimestamp?: string;
  liveLabels?: { onLive?: string; historic?: string };
  className?: string;
  timezoneLabel?: string;
  onOpenChange?: (open: boolean) => void;
  isDisableToOpenMenu?: boolean;
  inputIsUTC?: boolean;
  resetLabel?: string;
}

export const TimestampCalendarPicker = (props: CalendarProps) => {
  const {
    handleChangeDatetime,
    isLive,
    reset,
    displayTimestamp,
    initialTimestamp,
    liveLabels,
    className = '',
    timezoneLabel = 'UTC',
    onOpenChange,
    isDisableToOpenMenu = false,
    inputIsUTC = false,
    resetLabel = 'Reset',
  } = props;

  const baseInitialTs = initialTimestamp || displayTimestamp || moment().toISOString();

  const initialMoment = inputIsUTC ? moment.utc(baseInitialTs) : moment(baseInitialTs);
  const [date, setDate] = useState<string>(initialMoment.format('DD/MM/YYYY'));
  const [time, setTime] = useState<string>(initialMoment.format('HH:mm'));
  const [datetime, setDatetime] = useState<string>(initialMoment.toISOString());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const liveProps = {
    onLive: {
      icon: faSignalStream,
      text: liveLabels?.onLive || 'Live data',
      className: 'signal-live-data',
    },
    historic: {
      icon: faSignalStreamSlash,
      text: liveLabels?.historic || 'Historic Data',
      className: 'signal-historic-data',
    },
  }[isLive ? 'onLive' : 'historic'];

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isDisableToOpenMenu) return;

    setAnchorEl(event.currentTarget);
    onOpenChange?.(true);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
    onOpenChange?.(false);
  };

  const handleApply = () => {
    handleClose();
    handleChangeDatetime(moment(datetime).toISOString());
  };

  const handleUpdateCalendar = (value: Date) => {
    try {
      const [hour, minute] = time.split(':');

      const m = inputIsUTC
        ? moment.utc({
            year: value.getFullYear(),
            month: value.getMonth(),
            date: value.getDate(),
            hour: parseInt(hour),
            minute: parseInt(minute),
          })
        : moment({
            year: value.getFullYear(),
            month: value.getMonth(),
            date: value.getDate(),
            hour: parseInt(hour),
            minute: parseInt(minute),
          });

      setDatetime(m.toISOString());
      setDate((inputIsUTC ? m.utc() : m).format('DD/MM/YYYY'));
      setTime((inputIsUTC ? m.utc() : m).format('HH:mm'));
    } catch (error) {
      console.error(error);
      return;
    }
  };

  const checkIfIsNumberValid = (value: string) => {
    if (value.includes('_')) return false;

    return !isNaN(parseInt(value));
  };

  const handleChangeTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTimeString = e.target.value || '';

    const [hour, minute] = newTimeString.split(':');

    if (
      checkIfIsNumberValid(hour) &&
      checkIfIsNumberValid(minute) &&
      parseInt(hour) <= 23 &&
      parseInt(minute) <= 59
    ) {
      const currentDate = inputIsUTC ? moment.utc(datetime) : moment(datetime);

      currentDate.set({
        hour: parseInt(hour),
        minute: parseInt(minute),
      });

      if (currentDate.isValid()) {
        setDatetime(currentDate.toISOString());
      }
    }

    setTime(newTimeString);
  };

  const handleChangeDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDateString = e.target.value || '';

    if (!newDateString.includes('_')) {
      const splitDateArr = newDateString.split('/');
      const [day, month, year] = splitDateArr;

      if (!isNaN(parseInt(day)) && !isNaN(parseInt(month)) && !isNaN(parseInt(year))) {
        const currentDate = inputIsUTC ? moment.utc(datetime) : moment(datetime);

        currentDate.set({
          date: parseInt(day),
          month: parseInt(month) - 1,
          year: parseInt(year),
        });

        setDatetime(currentDate.toISOString());
      }
    }
    setDate(newDateString);
  };

  const handleReset = () => {
    reset();
  };

  const renderButtons = () => {
    const baseTs = displayTimestamp || datetime;
    const m = inputIsUTC ? moment.utc(baseTs) : moment(baseTs);
    const formatted = m.format('DD MMM YYYY - HH:mm');
    return (
      <div className={`button-container ${className}`}>
        <button
          aria-controls={open ? 'demo-customized-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          className={`timestamp-btn ${liveProps.className}`}
        >
          <div className="vol-surface-timestamp-info">
            <div className="live-data-container">
              <FontAwesomeIcon icon={liveProps.icon} className="signal-icon" />
              <span className="live-text">{liveProps.text}</span>
            </div>
            <div className="date-container">
              <FontAwesomeIcon icon={faCalendar} className="calendar-icon" />
              <span className="date-text">
                {formatted} {timezoneLabel}
              </span>
            </div>
          </div>
        </button>

        {!isLive && (
          <Button
            aria-controls={open ? 'demo-customized-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            disableElevation
            onClick={handleReset}
            className="reset-btn"
          >
            {resetLabel}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className={`timestamp-calendar-picker-root ${className}`}>
      {renderButtons()}
      <Menu
        sx={{ width: '20.5rem' }}
        slotProps={{
          paper: {
            className: 'vol-surface-calendar-menu',
            sx: {
              backgroundColor: '#002649 !important',
            },
          },
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <Box className="vol-surface-calendar">
          <CustomCalendar value={datetime} handler={handleUpdateCalendar} newVersion />
          <Box sx={{ display: 'flex', rowGap: 2, justifyContent: 'space-between', padding: 2 }}>
            <InputMask
              mask="99/99/9999"
              value={date}
              onChange={handleChangeDate}
              className="input-date"
            >
              <TextField size="small" margin="dense" type="text" sx={{ width: '60%' }} />
            </InputMask>
            <InputMask mask="99:99" value={time} onChange={handleChangeTime} className="input-date">
              <TextField size="small" margin="dense" type="text" sx={{ width: '30%' }} />
            </InputMask>
          </Box>
          <Box className="button-wrap">
            <Button
              variant="outlined"
              size="small"
              sx={{ width: '100%', borderColor: '#B0C4D4', color: '#B0C4D4' }}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{ width: '100%', background: '#B0C4D4' }}
              onClick={handleApply}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Menu>
    </div>
  );
};
